/**
 * One GeometryInstance3D's link to the scene cull: the node data React writes after each commit,
 * and the draw state the cull writes before each render. A drawer component and a GLB mesh, which
 * Godot imports as a MeshInstance3D, each hold one.
 */
import * as THREE from 'three';
import { geometryFade } from '../../godot/rendering/fadeAlpha';
import { hasSurface } from '../../godot/math/aabb';
import { hasVisibilityRange, NO_VISIBILITY_RANGE, } from '../../godot/rendering/visibilityRange';
import { compareTreeOrder } from '../contexts/TreeOrderContext';
import { copyAabb, UNPLACED } from './placements';
/** Scratch for one `isPlaced`, `worldBox` or `isIndexed` call: the cull measures one instance at a time. */
const nodeMatrix = new THREE.Matrix4();
const ownBox = new THREE.Box3();
const ownSize = new THREE.Vector3();
/**
 * One instance's link to the scene cull. React writes its inputs after each commit. Before each
 * render the cull writes `isVisible`, which the draw hooks read through `DrawnInstance`, and the
 * fade of each surface its drawer adds.
 */
export class CulledInstance {
    isVisible = true;
    links = { path: null, parentPath: null, order: [], hasRange: false };
    range = NO_VISIBILITY_RANGE;
    /** Whether its render instance has a geometry base (`godot/scene/geometryBase.ts`). */
    hasBase = false;
    transparency = 0;
    customAabb = null;
    placement = UNPLACED;
    surfaces = new Set();
    /** Takes the node's data. A change of links hands the cull a new links object. */
    update(place, properties) {
        const hasRange = hasVisibilityRange(properties.visibilityRange);
        if (!hasSameLinks(this.links, place, hasRange))
            this.links = { ...place, hasRange };
        this.range = properties.visibilityRange;
        this.transparency = properties.transparency;
        this.customAabb = properties.customAabb;
    }
    /**
     * Whether its pose and its box are known: `custom_aabb`, or the box its drawer measures. The cull
     * indexes no instance without a base, so its box is never read.
     */
    get isPlaced() {
        if (!this.hasBase)
            return true;
        if (!this.placement.nodeMatrixWorld(nodeMatrix))
            return false;
        return this.customAabb !== null || this.placement.ownAabb(ownBox);
    }
    /** A base whose box has a surface (`renderer_scene_cull.cpp:1675-1681`), on a node visible in the tree. */
    get isIndexed() {
        if (!this.hasBase || !this.placement.isVisibleInTree())
            return false;
        if (this.customAabb)
            return hasSurface(this.customAabb.size);
        return this.placement.ownAabb(ownBox) && hasSurface(ownBox.getSize(ownSize));
    }
    worldBox(target) {
        // `custom_aabb` replaces the instance's own (`renderer_scene_cull.cpp:1988-1992`).
        if (this.customAabb)
            copyAabb(target, this.customAabb);
        // An instance without a box is never indexed: an empty box leaves no stale scratch behind.
        else if (!this.placement.ownAabb(target))
            target.makeEmpty();
        this.placement.nodeMatrixWorld(nodeMatrix);
        target.applyMatrix4(nodeMatrix);
    }
    nodeMatrixWorld(target) {
        return this.placement.nodeMatrixWorld(target);
    }
    apply(isVisible, rangeFade) {
        this.isVisible = isVisible;
        const fade = geometryFade(this.transparency, rangeFade);
        for (const surface of this.surfaces)
            surface.applyFade(fade);
    }
    add(surface) {
        this.surfaces.add(surface);
        return () => this.surfaces.delete(surface);
    }
}
function hasSameLinks(links, place, hasRange) {
    return (links.path === place.path &&
        links.parentPath === place.parentPath &&
        links.hasRange === hasRange &&
        compareTreeOrder(links.order, place.order) === 0);
}
//# sourceMappingURL=culledInstance.js.map